/* Linsoul video stories: click-to-load YouTube player, playlist swap, GSAP entrance, sliding row highlight,
   magnetic play button. Everything works without GSAP; motion is skipped under reduced motion. */
(() => {
  'use strict';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');

  const loadGsap = (src) => {
    if (window.gsap) return Promise.resolve(window.gsap);
    if (window.lsLoadGsap) return window.lsLoadGsap(src);
    return new Promise((resolve, reject) => {
      const tag = document.createElement('script');
      tag.src = src;
      tag.async = true;
      tag.onload = () => (window.gsap ? resolve(window.gsap) : reject());
      tag.onerror = reject;
      document.head.appendChild(tag);
    });
  };
  const offscreen = (el) => {
    const r = el.getBoundingClientRect();
    return r.top > window.innerHeight || r.bottom < 0;
  };
  const pad = (n) => String(n).padStart(2, '0');

  function init(root) {
    if (root.dataset.lstReady) return;
    root.dataset.lstReady = '1';
    const player = root.querySelector('[data-lst-player]');
    const cover = root.querySelector('[data-lst-play]');
    const media = root.querySelector('[data-lst-media]');
    const poster = root.querySelector('[data-lst-poster]');
    const title = root.querySelector('[data-lst-title]');
    const guest = root.querySelector('[data-lst-guest]');
    const index = root.querySelector('[data-lst-index]');
    const link = root.querySelector('[data-lst-link]');
    const magnet = root.querySelector('[data-lst-magnet]');
    const list = root.querySelector('[data-lst-list]');
    const indicator = root.querySelector('[data-lst-indicator]');
    const eps = [...root.querySelectorAll('[data-lst-ep]')];
    if (!player || !cover || !eps.length) return;
    let current = eps.find((e) => e.getAttribute('aria-current') === 'true') || eps[0];
    let gsap = null;

    /* Sliding highlight behind the current row */
    const placeIndicator = (animate) => {
      if (!indicator) return;
      const row = current.closest('li');
      const y = row.offsetTop;
      const h = row.offsetHeight;
      if (gsap && animate && !reduce.matches) {
        gsap.to(indicator, { y, height: h, opacity: 1, duration: 0.55, ease: 'power3.out', overwrite: 'auto' });
      } else {
        indicator.style.transform = `translateY(${y}px)`;
        indicator.style.height = `${h}px`;
        indicator.style.opacity = '1';
        if (gsap) gsap.set(indicator, { y, height: h, opacity: 1 });
      }
      root.classList.add('has-indicator');
    };
    const keepInList = () => {
      if (!list || list.scrollHeight <= list.clientHeight + 2) return;
      const row = current.closest('li');
      const top = row.offsetTop - list.scrollTop;
      if (top < 0 || top + row.offsetHeight > list.clientHeight) {
        list.scrollTo({ top: row.offsetTop - 10, behavior: reduce.matches ? 'auto' : 'smooth' });
      }
    };

    const play = (ep) => {
      const id = encodeURIComponent(ep.dataset.id);
      const start = parseInt(ep.dataset.start, 10) || 0;
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&playsinline=1&rel=0&modestbranding=1${start ? `&start=${start}` : ''}`;
      iframe.title = ep.dataset.title || '';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      player.querySelector('iframe')?.remove();
      cover.hidden = true;
      player.appendChild(iframe);
      root.classList.add('is-playing');
      if (gsap && !reduce.matches) gsap.fromTo(iframe, { autoAlpha: 0, scale: 1.015 }, { autoAlpha: 1, scale: 1, duration: 0.6, ease: 'power3.out', clearProps: 'transform' });
      iframe.focus({ preventScroll: true });
    };

    const select = (ep) => {
      current.removeAttribute('aria-current');
      ep.setAttribute('aria-current', 'true');
      current = ep;
      if (poster) poster.src = ep.dataset.poster;
      if (title) title.textContent = ep.dataset.title || '';
      if (guest) { guest.textContent = ep.dataset.guest || ''; guest.hidden = !ep.dataset.guest; }
      if (index) index.textContent = pad(eps.indexOf(ep) + 1);
      if (link) link.href = ep.dataset.url;
      cover.setAttribute('aria-label', (cover.dataset.playLabel || '').replace('{title}', ep.dataset.title || ''));
      placeIndicator(true);
      keepInList();
    };

    cover.addEventListener('click', () => play(current));
    eps.forEach((ep) => ep.addEventListener('click', () => {
      select(ep);
      play(ep);
      const r = player.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) {
        const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ls-header-offset')) || 0;
        window.scrollTo({ top: window.scrollY + r.top - offset - 16, behavior: reduce.matches ? 'auto' : 'smooth' });
      }
    }));
    const warm = (ep) => { if (!ep.dataset.warm) { ep.dataset.warm = '1'; new Image().src = ep.dataset.poster; } };
    eps.forEach((ep) => {
      ep.addEventListener('pointerenter', () => warm(ep));
      ep.addEventListener('focus', () => warm(ep));
    });

    placeIndicator(false);
    if ('ResizeObserver' in window && list) new ResizeObserver(() => placeIndicator(false)).observe(list);

    /* GSAP: entrance + pointer physics */
    if (!('IntersectionObserver' in window)) return;
    const near = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      near.disconnect();
      loadGsap(root.dataset.gsapSrc).then((g) => {
        gsap = g;
        placeIndicator(false);
        const mm = gsap.matchMedia();

        mm.add('(prefers-reduced-motion: no-preference)', () => {
          if (!offscreen(root)) return;
          const head = root.querySelectorAll('[data-lst-reveal]');
          const img = media?.querySelector('img');
          const meta = root.querySelectorAll('.lst-player__meta > *, .lst-player__play');
          const rows = root.querySelectorAll('.lst-ep-item, .lst-side__head, .lst-bar');
          gsap.set(head, { autoAlpha: 0, y: 28 });
          const clipFrom = 'inset(7% 7% 7% 7% round 28px)';
          const clipTo = 'inset(0% 0% 0% 0% round 20px)';
          gsap.set(player, { clipPath: clipFrom });
          if (img) gsap.set(img, { scale: 1.18 });
          gsap.set(meta, { autoAlpha: 0, y: 14 });
          gsap.set(rows, { autoAlpha: 0, y: 18 });
          if (indicator) gsap.set(indicator, { autoAlpha: 0 });

          const tl = gsap.timeline({ paused: true, defaults: { ease: 'expo.out' } });
          tl.to(head, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.08 }, 0)
            .fromTo(player, { clipPath: clipFrom }, { clipPath: clipTo, duration: 1.3, ease: 'expo.inOut', clearProps: 'clipPath', immediateRender: false }, 0.1)
            .to(img || [], { scale: 1, duration: 1.6, ease: 'expo.out', clearProps: 'transform' }, 0.2)
            .to(meta, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.06, clearProps: 'transform,opacity,visibility' }, 0.75)
            .to(rows, { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.05, clearProps: 'transform,opacity,visibility' }, 0.45)
            .to(indicator || [], { autoAlpha: 1, duration: 0.5 }, 0.9);

          const enter = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            enter.disconnect();
            tl.play();
          }, { rootMargin: '0px 0px -15% 0px' });
          enter.observe(root);
          return () => { enter.disconnect(); tl.kill(); };
        });

        mm.add('(prefers-reduced-motion: no-preference) and (hover: hover) and (pointer: fine)', () => {
          if (!magnet || !media) return;
          const img = media.querySelector('img');
          const mx = gsap.quickTo(magnet, 'x', { duration: 0.6, ease: 'power3.out' });
          const my = gsap.quickTo(magnet, 'y', { duration: 0.6, ease: 'power3.out' });
          const ix = img ? gsap.quickTo(img, 'x', { duration: 1.2, ease: 'power3.out' }) : null;
          const iy = img ? gsap.quickTo(img, 'y', { duration: 1.2, ease: 'power3.out' }) : null;
          const clamp = gsap.utils.clamp(-22, 22);
          const move = (e) => {
            const b = magnet.getBoundingClientRect();
            mx(clamp((e.clientX - (b.left + b.width / 2)) * 0.2));
            my(clamp((e.clientY - (b.top + b.height / 2)) * 0.2));
            if (ix) {
              const r = cover.getBoundingClientRect();
              ix(((e.clientX - r.left) / r.width - 0.5) * -14);
              iy(((e.clientY - r.top) / r.height - 0.5) * -10);
            }
          };
          const enter = () => {
            gsap.to(magnet, { scale: 1.08, duration: 0.5, ease: 'back.out(2)' });
            if (img) gsap.to(img, { scale: 1.04, duration: 1.2, ease: 'power3.out' });
          };
          const leave = () => {
            mx(0); my(0);
            if (ix) { ix(0); iy(0); }
            gsap.to(magnet, { scale: 1, duration: 0.5, ease: 'power3.out' });
            if (img) gsap.to(img, { scale: 1, duration: 1.2, ease: 'power3.out' });
          };
          cover.addEventListener('pointermove', move);
          cover.addEventListener('pointerenter', enter);
          cover.addEventListener('pointerleave', leave);
          return () => {
            cover.removeEventListener('pointermove', move);
            cover.removeEventListener('pointerenter', enter);
            cover.removeEventListener('pointerleave', leave);
            gsap.set([magnet, img].filter(Boolean), { clearProps: 'transform' });
          };
        });
      }).catch(() => {});
    }, { rootMargin: '600px 0px' });
    near.observe(root);
  }

  const run = () => document.querySelectorAll('[data-lst]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
